import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useSetMenu } from '../../../hooks/useSetMenu';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import LoadingOverlay from '../../Generic/LoadingOverlay';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const ViewEvaluation = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mainStore, evaluationStore } = useStore();
    const axiosPrivate = useAxiosPrivate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showStats, setShowStats] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [evaluation, setEvaluation] = useState(null);
    const [questionTypes, setQuestionTypes] = useState({});

    useSetMenu(mainStore.EvaluationMenuType, mainStore.EvaluationMenuNo);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchEvaluationInstance = async () => {
            try {
                const typesResponse = await axiosPrivate.get(
                    `/QuestionTypes/GetQuestionTypeByUniversity/${mainStore.universityId}`,
                    { signal: controller.signal }
                );
                
                if (typesResponse.data) {
                    const typesMap = {};
                    typesResponse.data.forEach(type => {
                        typesMap[type.id] = type;
                    });
                    setQuestionTypes(typesMap);
                }
                
                const response = await axiosPrivate.get(`/EvaluationInstances/${id}`, { signal: controller.signal });
                
                if (response.data) {
                    if (response.data.answers && Array.isArray(response.data.answers)) {
                        response.data.answers = response.data.answers.map(answer => {
                            if (!answer.hasOwnProperty('freeText')) {
                                answer.freeText = '';
                            }
                            
                            if (!answer.selectedOptions) {
                                answer.selectedOptions = [];
                            }
                            
                            if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0 && answer.options) {
                                const options = answer.options.filter(opt => 
                                    answer.selectedOptionIds.includes(opt.id || opt.questionOptionId)
                                );
                                
                                if (options.length > 0) {
                                    answer.selectedOptions = options;
                                }
                            }
                            
                            return answer;
                        });
                    }
                    
                    setEvaluation(response.data);
                    evaluationStore.setCurrentInstance(response.data);
                    
                    if (response.data.answers && response.data.answers.length > 0) {
                        prepareChartData(response.data.answers);
                    }
                } else {
                    setError(t('noEvaluationFound'));
                }
            } catch (err) {
                console.error('Error fetching evaluation:', err);
                setError(err.response?.data?.message || t('Error fetching evaluation'));
                enqueueSnackbar(t('errorLoadingEvaluation'), { variant: 'error' });
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchEvaluationInstance();

        return () => {
            isMounted = false;
            controller.abort();
            evaluationStore.clearCurrentInstance();
        };
    }, [axiosPrivate, evaluationStore, id, t, mainStore.universityId, enqueueSnackbar]);

    const prepareChartData = (answers) => {
        const choiceQuestions = answers.filter(a => 
            a.selectedOptions && a.selectedOptions.length > 0
        );
        
        if (choiceQuestions.length > 0) {
            const firstQuestion = choiceQuestions[0];
            
            if (firstQuestion.selectedOptions) {
                const labels = firstQuestion.selectedOptions.map(o => o.optionLabel);
                const data = firstQuestion.selectedOptions.map(o => 1);
                
                const chartData = {
                    labels,
                    datasets: [
                        {
                            label: 'Responses',
                            data,
                            backgroundColor: [
                                'rgba(255, 99, 132, 0.7)',
                                'rgba(54, 162, 235, 0.7)',
                                'rgba(255, 206, 86, 0.7)',
                                'rgba(75, 192, 192, 0.7)',
                                'rgba(153, 102, 255, 0.7)',
                            ],
                            borderColor: [
                                'rgba(255, 99, 132, 1)',
                                'rgba(54, 162, 235, 1)',
                                'rgba(255, 206, 86, 1)',
                                'rgba(75, 192, 192, 1)',
                                'rgba(153, 102, 255, 1)',
                            ],
                            borderWidth: 1,
                        },
                    ],
                };
                
                setChartData(chartData);
            }
        }
    };

    const renderAnswerValue = (answer) => {
        if (!answer) return <span className="text-muted">{t('noAnswerProvided')}</span>;
        
        if (answer.freeText && answer.freeText.trim() !== '') {
            return (
                <div className="border p-2 rounded">{answer.freeText}</div>
            );
        }
        
        if (answer.selectedOptions && answer.selectedOptions.length > 0) {
            return (
                <ul className="list-group">
                    {answer.selectedOptions.map(option => (
                        <li key={option.id || option.questionOptionId} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                                <span className="badge bg-primary me-2">{option.optionCode}</span>
                                {option.optionLabel}
                            </div>
                            {option.value && (
                                <span className="badge bg-info">{option.value}</span>
                            )}
                        </li>
                    ))}
                </ul>
            );
        }
        
        if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0 && answer.options) {
            const selectedOptions = answer.options.filter(opt => 
                answer.selectedOptionIds.includes(opt.id || opt.questionOptionId)
            );
            
            if (selectedOptions.length > 0) {
                return (
                    <ul className="list-group">
                        {selectedOptions.map(option => (
                            <li key={option.id || option.questionOptionId} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="badge bg-primary me-2">{option.optionCode}</span>
                                    {option.optionLabel}
                                </div>
                                {option.value && (
                                    <span className="badge bg-info">{option.value}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                );
            }
        }
        
        return <span className="text-muted">{t('noAnswerProvided')}</span>;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    const exportToCSV = () => {
        if (!evaluationStore.currentInstance || !evaluationStore.currentInstance.answers) {
            enqueueSnackbar(t('noDataToExport'), { variant: 'warning' });
            return;
        }
        
        const { evaluationTemplateName, answers } = evaluationStore.currentInstance;
        
        let csvContent = "Question,Answer\n";
        
        answers.forEach(answer => {
            let questionText = `"${answer.questionText.replace(/"/g, '""')}"`;
            
            let answerText = '';
            if (answer.freeText && answer.freeText.trim() !== '') {
                answerText = `"${answer.freeText.replace(/"/g, '""')}"`;
            } else if (answer.selectedOptions && answer.selectedOptions.length > 0) {
                const options = answer.selectedOptions.map(option => 
                    `${option.optionCode} - ${option.optionLabel}`
                ).join('; ');
                answerText = `"${options.replace(/"/g, '""')}"`;
            } else {
                answerText = '"No answer provided"';
            }
            
            csvContent += `${questionText},${answerText}\n`;
        });
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${evaluationTemplateName.replace(/\s+/g, '_')}_evaluation.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const goToReport = () => {
        if (evaluation && evaluation.evaluationTemplateId) {
            navigate(`/Evaluation/Report/${evaluation.evaluationTemplateId}`);
        }
    };

    if (loading) {
        return <LoadingOverlay />;
    }
    
    if (error) {
        return (
            <div className="container py-4">
                <div className="card rounded-0 borderLine">
                    <div className="card-header pb-0 rounded-0 bg-danger cardHeaderBackground">
                        <p className="crudTitle">{t('evaluationView')}</p>
                    </div>
                    <div className="card-body p-3">
                        <div className="alert alert-danger">{error}</div>
                        <button 
                            className="btn btn-secondary"
                            onClick={() => navigate('/Evaluation/List')}
                        >
                            {t('backToList')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    if (!evaluation) {
        return (
            <div className="container py-4">
                <div className="card rounded-0 borderLine">
                    <div className="card-header pb-0 rounded-0 bg-danger cardHeaderBackground">
                        <p className="crudTitle">{t('evaluationView')}</p>
                    </div>
                    <div className="card-body p-3">
                        <div className="alert alert-warning">{t('noEvaluationFound')}</div>
                        <button 
                            className="btn btn-secondary"
                            onClick={() => navigate('/Evaluation/List')}
                        >
                            {t('backToList')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const { 
        evaluationTemplateName, 
        instanceStatusName, 
        submittedDate,
        answers,
        subjectName,
        studentName
    } = evaluationStore.currentInstance;

    return (
        <div className="container py-4">
            {}
            <div className="card rounded-0 borderLine mb-4">
                <div className="card-header pb-0 rounded-0 bg-danger cardHeaderBackground">
                    <div className="d-flex justify-content-between align-items-center">
                        <p className="crudTitle mb-0">{t('evaluationView')}: {evaluationTemplateName}</p>
                        <div>
                            <button
                                className="btn btn-outline-light btn-sm me-2"
                                onClick={goToReport}
                            >
                                <i className="bi bi-bar-chart me-1"></i>
                                {t('viewReports')}
                            </button>
                            <button
                                className="btn btn-outline-light btn-sm"
                                onClick={() => navigate('/Evaluation/List')}
                            >
                                <i className="bi bi-arrow-left me-1"></i>
                                {t('back')}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body p-3">
                    <div className="row">
                        <div className="col-md-4">
                            <p><strong>{t('templateName')}:</strong> {evaluationTemplateName}</p>
                            <p><strong>{t('templateDescription')}:</strong> {evaluation.evaluationTemplateDescription || '-'}</p>
                        </div>
                        <div className="col-md-4">
                            <p><strong>{t('subject')}:</strong> {subjectName || '-'}</p>
                            <p><strong>{t('student')}:</strong> {studentName || '-'}</p>
                        </div>
                        <div className="col-md-4">
                            <p>
                                <strong>{t('status')}:</strong> 
                                <span className={`ms-2 badge ${
                                    instanceStatusName === 'Pending' ? 'bg-warning' : 
                                    instanceStatusName === 'Submitted' ? 'bg-success' : 
                                    'bg-secondary'
                                }`}>
                                    {instanceStatusName}
                                </span>
                            </p>
                            <p><strong>{t('submittedDate')}:</strong> {submittedDate ? formatDate(submittedDate) : '-'}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {}
            {chartData && (
                <div className="row mb-4">
                    <div className="col-12">
                        <button 
                            className="btn btn-outline-primary mb-3"
                            onClick={() => setShowStats(!showStats)}
                        >
                            {showStats ? t('hideVisualization') : t('showVisualization')}
                        </button>
                        
                        {showStats && (
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="chart-container" style={{ height: '300px' }}>
                                        <Pie data={chartData} options={{ maintainAspectRatio: false }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {}
            <div className="row mb-4">
                <div className="col-12">
                    <button 
                        className="btn btn-outline-success"
                        onClick={exportToCSV}
                    >
                        <i className="bi bi-file-earmark-excel me-2"></i>
                        {t('exportToCSV')}
                    </button>
                </div>
            </div>
            
            {}
            <div className="card rounded-0 borderLine">
                <div className="card-header pb-0 rounded-0 bg-info cardHeaderBackground">
                    <p className="crudTitle">{t('evaluationAnswers')}</p>
                </div>
                <div className="card-body p-3">
                    {}
                    {(evaluation.evaluationQuestions && evaluation.evaluationQuestions.length > 0) ? (
                        <div className="accordion" id="questionAccordion">
                            {evaluation.evaluationQuestions.map((question, index) => (
                                <div className="accordion-item" key={question.id || index}>
                                    <h2 className="accordion-header" id={`heading${index}`}>
                                        <button
                                            className="accordion-button"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#collapse${index}`}
                                            aria-expanded={index === 0 ? "true" : "false"}
                                            aria-controls={`collapse${index}`}
                                        >
                                            <span className="me-2">{index + 1}.</span>
                                            {question.questionText}
                                            <span className="ms-2 badge bg-secondary">{question.questionTypeName}</span>
                                        </button>
                                    </h2>
                                    <div
                                        id={`collapse${index}`}
                                        className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                                        aria-labelledby={`heading${index}`}
                                        data-bs-parent="#questionAccordion"
                                    >
                                        <div className="accordion-body">
                                            <div className="mb-3">
                                                <label className="form-label"><strong>{t('answer')}</strong></label>
                                                {renderAnswerValue(question.answer)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (evaluation.answers && evaluation.answers.length > 0) ? (
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th width="50%">{t('question')}</th>
                                        <th>{t('answer')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {evaluation.answers.map((answer, index) => (
                                        <tr key={answer.id || index}>
                                            <td>
                                                <strong>{index + 1}. {answer.questionText}</strong>
                                                {answer.questionTypeName && (
                                                    <span className="ms-2 badge bg-secondary">{answer.questionTypeName}</span>
                                                )}
                                            </td>
                                            <td>{renderAnswerValue(answer)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="alert alert-info">{t('noAnswersFound')}</div>
                    )}
                </div>
            </div>
            
            <div className="d-flex justify-content-end mt-4">
                <button 
                    className="btn btn-secondary"
                    onClick={() => navigate('/Evaluation/List')}
                >
                    {t('backToList')}
                </button>
            </div>
        </div>
    );
};

export default ViewEvaluation; 